import { useState } from "react"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
  QueryFunctionContext,
} from "@tanstack/react-query"
import { useDehydratedState } from "source/shared/hooks/useDehydratedState"
import { APIErrorRecord } from "./shared/types"

const defaultQueryFn = ({ queryKey }: QueryFunctionContext) => {
  const [path, queryParams = {}] = queryKey
  return sessionApiClient.get(path, queryParams)
}

export const queryFnWithCatch = ({ queryKey }: QueryFunctionContext) => {
  const [path, queryParams = {}] = queryKey
  return sessionApiClient.get(path, queryParams).catch((error: Error) => error)
}

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: defaultQueryFn,
        throwOnError: (error: Error | APIErrorRecord) =>
          ["500"].includes((error as APIErrorRecord).status),
        staleTime: 60 * 1000,
        gcTime: typeof document === "undefined" ? Infinity : 60 * 5 * 1000,
      },
    },
  })
}

// The browser-wide singleton. Server code must never use this client —
// it would share one cache across every request/organization.
export const queryClient = makeQueryClient()

export function ReactQueryProvider({
  children,
}: {
  children: React.ReactNode
}) {
  // On the server each render gets a fresh client so requests can't see
  // each other's cache; in the browser the singleton is shared app-wide.
  const [client] = useState(() =>
    typeof document === "undefined" ? makeQueryClient() : queryClient,
  )
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}

// useDehydratedState reads route loader data via useMatches, which requires
// a data router. Only render this inside RootLayout's tree, never around
// standalone trees like launchAsyncModal's that have no router at all.
export function RouteDataHydrationBoundary({
  children,
}: {
  children: React.ReactNode
}) {
  const dehydratedState = useDehydratedState()
  return (
    <HydrationBoundary state={dehydratedState}>{children}</HydrationBoundary>
  )
}
